import React from "react";
import Dropzone from "react-dropzone";
import { Upload, FileText, AlertCircle } from "lucide-react";
import { LoaderV2, LoaderMode, LoaderTheme } from "components/common/LoaderV2/LoaderV2";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../CreateProjectSheet.constants";

// Loading messages for script parsing
const scriptParsingTexts = [
  "Analyzing script structure...",
  "Extracting characters and scenes...",
  "Processing dialogue and action lines...",
  "Identifying scene locations...",
  "Parsing character breakdowns...",
  "Validating script format...",
  "Organizing scene information...",
  "Finalizing project setup...",
];

interface FileUploadSectionProps {
  file: File | null;
  fileLoading: boolean;
  fileError: string | null;
  handleFileUpload: (
    acceptedFiles: File[],
    fileRejections: any[],
    mode?: string,
    existingProject?: any
  ) => void;
  mode?: string;
  existingProject?: any;
}

export const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  file,
  fileLoading,
  fileError,
  handleFileUpload,
  mode,
  existingProject,
}) => {
  const handleDrop = (acceptedFiles: File[], fileRejections: any[]) => {
    handleFileUpload(acceptedFiles, fileRejections, mode, existingProject);
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-medium text-foreground">Screenplay Upload</h3>
      <Dropzone
        onDrop={handleDrop}
        accept={ACCEPTED_FILE_TYPES}
        maxSize={MAX_FILE_SIZE}
        maxFiles={1}
        disabled={fileLoading}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragActive
                ? "border-primary bg-primary/10"
                : fileError
                  ? "border-danger bg-danger/10"
                  : "border-border hover:border-primary/50"
            }`}
          >
            <input {...getInputProps()} />
            {fileLoading ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div className="text-center">
                  <h4 className="text-sm font-medium text-foreground mb-2">
                    Processing Screenplay
                  </h4>
                  <LoaderV2
                    mode={LoaderMode.inline}
                    theme={LoaderTheme.brand}
                    size="sm"
                    rotatingTexts={scriptParsingTexts}
                    textInterval={1800}
                    textClassName="text-muted-foreground text-xs"
                  />
                </div>
              </div>
            ) : file ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="w-8 h-8 text-primary" />
                <p className="text-sm font-medium text-foreground">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">
                  {isDragActive ? "Drop screenplay here" : "Upload screenplay"}
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF, FDX, Celtx, or Fountain files up to 20MB
                </p>
              </div>
            )}
          </div>
        )}
      </Dropzone>
      {fileError && (
        <div className="flex items-center gap-2 text-danger text-sm">
          <AlertCircle className="w-4 h-4" />
          {fileError}
        </div>
      )}
    </div>
  );
};
